import React, { useState, useEffect, useCallback } from 'react';
import { Socket } from 'socket.io-client';

// ============ TYPES ============

interface SiegeStatus {
  active: boolean;
  siege?: {
    attackerName: string;
    defenderName: string;
    progress: number;
    phase: 'bombardment' | 'breach' | 'final_assault';
    timeRemaining: number;
    attackerCount: number;
    defenderCount: number;
    volleyCount: number;
    defenseCount: number;
    isFortified: boolean;
    isRallied: boolean;
  };
  cooldownEnd?: number;
}

interface VolleyResult {
  success: boolean;
  message: string;
  damage?: number;
  isCritical?: boolean;
  isMiss?: boolean;
  progress?: number;
  captured?: boolean;
}

interface DefenseResult {
  success: boolean;
  message: string;
  effect?: string;
  progress?: number;
}

interface CombatLogEntry {
  id: number;
  timestamp: number;
  type: 'volley' | 'defense' | 'rally' | 'system';
  message: string;
  isCritical?: boolean;
  isMiss?: boolean;
}

interface SiegePanelProps {
  isOpen: boolean;
  onClose: () => void;
  socket: Socket | null;
  islandId: string;
  islandName: string;
  playerId: string;
  playerName: string;
}

// ============ COMPONENT ============

export const SiegePanel: React.FC<SiegePanelProps> = ({
  isOpen,
  onClose,
  socket,
  islandId,
  islandName,
  playerId,
  playerName,
}) => {
  const [siegeStatus, setSiegeStatus] = useState<SiegeStatus | null>(null);
  const [playerSide, setPlayerSide] = useState<'attacker' | 'defender' | null>(null);
  const [combatLog, setCombatLog] = useState<CombatLogEntry[]>([]);
  const [cooldownRemaining, setCooldownRemaining] = useState<number>(0);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const logIdRef = React.useRef<number>(0);

  const addLogEntry = useCallback((type: CombatLogEntry['type'], message: string, isCritical?: boolean, isMiss?: boolean): void => {
    logIdRef.current += 1;
    const newId = logIdRef.current;
    setCombatLog(prevLog => [
      { id: newId, timestamp: Date.now(), type, message, isCritical, isMiss },
      ...prevLog.slice(0, 49),
    ]);
  }, []);

  // Fetch siege status on mount and periodically
  useEffect(() => {
    if (!socket || !isOpen || !islandId) return;

    const fetchStatus = (): void => {
      socket.emit('territory:getSiegeStatus', { islandId });
    };

    fetchStatus();
    const interval = setInterval(fetchStatus, 1000);

    return () => clearInterval(interval);
  }, [socket, isOpen, islandId]);

  // Handle socket events
  useEffect(() => {
    if (!socket) return;

    const handleSiegeStatus = (data: { islandId: string } & SiegeStatus): void => {
      if (data.islandId === islandId) {
        setSiegeStatus({ active: data.active, siege: data.siege, cooldownEnd: data.cooldownEnd });
      }
    };

    const handleVolleyResult = (data: VolleyResult & { islandId: string }): void => {
      if (data.islandId === islandId) {
        setActionMessage(data.message);
        if (data.success) {
          if (data.isMiss) {
            addLogEntry('volley', `${playerName}: MISS!`, false, true);
          } else if (data.isCritical) {
            addLogEntry('volley', `${playerName}: CRITICAL HIT! ${data.damage} damage!`, true);
          } else {
            addLogEntry('volley', `${playerName}: ${data.damage} damage`);
          }
        }
        setTimeout(() => setActionMessage(null), 2000);
      }
    };

    const handleDefenseResult = (data: DefenseResult & { islandId: string }): void => {
      if (data.islandId === islandId) {
        setActionMessage(data.message);
        if (data.success && data.effect) {
          addLogEntry('defense', `${playerName}: ${data.effect}`);
        }
        setTimeout(() => setActionMessage(null), 2000);
      }
    };

    const handleSiegeVolley = (data: { islandId: string; attackerName: string; damage: number; isMiss: boolean; isCritical: boolean }): void => {
      if (data.islandId === islandId && data.attackerName !== playerName) {
        if (data.isMiss) {
          addLogEntry('volley', `${data.attackerName}: MISS!`, false, true);
        } else if (data.isCritical) {
          addLogEntry('volley', `${data.attackerName}: CRITICAL! ${data.damage} dmg`, true);
        } else {
          addLogEntry('volley', `${data.attackerName}: ${data.damage} dmg`);
        }
      }
    };

    const handleSiegeDefense = (data: { islandId: string; defenderName: string; action: string; effect: string }): void => {
      if (data.islandId === islandId && data.defenderName !== playerName) {
        addLogEntry('defense', `${data.defenderName}: ${data.effect}`);
      }
    };

    const handleSiegeRally = (data: { islandId: string; side: string; message: string }): void => {
      if (data.islandId === islandId) {
        addLogEntry('rally', data.message);
      }
    };

    const handleSiegeEnded = (data: { islandId: string; captured: boolean; attackerName: string; defenderName: string }): void => {
      if (data.islandId === islandId) {
        const msg = data.captured 
          ? `SIEGE COMPLETE! ${data.attackerName} has captured the territory!`
          : `SIEGE FAILED! ${data.defenderName} successfully defended!`;
        addLogEntry('system', msg);
        setTimeout(() => onClose(), 3000);
      }
    };

    const handleJoinedSiege = (data: { islandId: string; side: 'attacker' | 'defender' }): void => {
      if (data.islandId === islandId) {
        setPlayerSide(data.side);
        addLogEntry('system', `You joined as ${data.side}`);
      }
    };

    socket.on('territory:siegeStatus', handleSiegeStatus);
    socket.on('territory:volleyResult', handleVolleyResult);
    socket.on('territory:defenseResult', handleDefenseResult);
    socket.on('territory:siegeVolley', handleSiegeVolley);
    socket.on('territory:siegeDefense', handleSiegeDefense);
    socket.on('territory:siegeRally', handleSiegeRally);
    socket.on('territory:siegeEnded', handleSiegeEnded);
    socket.on('territory:joinedSiege', handleJoinedSiege);

    return () => {
      socket.off('territory:siegeStatus', handleSiegeStatus);
      socket.off('territory:volleyResult', handleVolleyResult);
      socket.off('territory:defenseResult', handleDefenseResult);
      socket.off('territory:siegeVolley', handleSiegeVolley);
      socket.off('territory:siegeDefense', handleSiegeDefense);
      socket.off('territory:siegeRally', handleSiegeRally);
      socket.off('territory:siegeEnded', handleSiegeEnded);
      socket.off('territory:joinedSiege', handleJoinedSiege);
    };
  }, [socket, islandId, playerName, addLogEntry, onClose]);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Cooldown timer
  useEffect(() => {
    if (cooldownRemaining <= 0) return;
    const interval = setInterval(() => {
      setCooldownRemaining(prev => Math.max(0, prev - 100));
    }, 100);
    return () => clearInterval(interval);
  }, [cooldownRemaining]);

  const handleJoinSiege = useCallback((side: 'attacker' | 'defender'): void => {
    if (!socket) return;
    socket.emit('territory:joinSiege', { islandId, side });
  }, [socket, islandId]);

  const handleFireVolley = useCallback((): void => {
    if (!socket || cooldownRemaining > 0) return;
    socket.emit('territory:fireVolley', { islandId });
    setCooldownRemaining(3000);
  }, [socket, islandId, cooldownRemaining]);

  const handleDefendAction = useCallback((action: 'repair' | 'fortify' | 'rally'): void => {
    if (!socket || cooldownRemaining > 0) return;
    socket.emit('territory:defendAction', { islandId, action });
    setCooldownRemaining(5000);
  }, [socket, islandId, cooldownRemaining]);

  const handleRallyAttackers = useCallback((): void => {
    if (!socket) return;
    socket.emit('territory:rallyAttackers', { islandId });
  }, [socket, islandId]);

  const handleStartSiege = useCallback((): void => {
    if (!socket) return;
    socket.emit('territory:startSiege', { islandId });
    setPlayerSide('attacker');
  }, [socket, islandId]);

  if (!isOpen) return null;

  const formatTime = (ms: number): string => {
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const getPhaseColor = (phase: string): string => {
    switch (phase) {
      case 'bombardment': return '#3fb950';
      case 'breach': return '#f0883e';
      case 'final_assault': return '#f85149';
      default: return '#8b949e';
    }
  };

  const getPhaseLabel = (phase: string): string => {
    switch (phase) {
      case 'bombardment': return 'BOMBARDMENT';
      case 'breach': return 'BREACH';
      case 'final_assault': return 'FINAL ASSAULT';
      default: return phase.toUpperCase();
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: '#0d1117',
          border: '2px solid #30363d',
          padding: 0,
          width: '600px',
          maxHeight: '90vh',
          overflow: 'hidden',
          fontFamily: '"Sometype Mono", monospace',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px 16px',
            borderBottom: '1px solid #30363d',
            backgroundColor: '#161b22',
          }}
        >
          <span style={{ color: '#f85149', fontSize: '14px', fontWeight: 'bold' }}>
            SIEGE: {islandName}
          </span>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: '1px solid #f85149',
              color: '#f85149',
              cursor: 'pointer',
              fontSize: '14px',
              padding: '4px 8px',
              fontFamily: '"Sometype Mono", monospace',
              fontWeight: 'bold',
            }}
          >
            x
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '16px' }}>
          {!siegeStatus?.active ? (
            // No active siege - show start siege or cooldown
            <div>
              {siegeStatus?.cooldownEnd && Date.now() < siegeStatus.cooldownEnd ? (
                <div
                  style={{
                    backgroundColor: '#2d1b1b',
                    padding: '16px',
                    border: '1px solid #f85149',
                    textAlign: 'center',
                  }}
                >
                  <div style={{ color: '#f85149', fontSize: '14px', fontWeight: 'bold', marginBottom: '8px' }}>
                    TERRITORY PROTECTED
                  </div>
                  <div style={{ color: '#8b949e', fontSize: '12px' }}>
                    Cooldown expires in {formatTime(siegeStatus.cooldownEnd - Date.now())}
                  </div>
                  <div style={{ color: '#8b949e', fontSize: '11px', marginTop: '8px' }}>
                    Failed sieges trigger a 24-hour protection period
                  </div>
                </div>
              ) : (
                <div>
                  <div
                    style={{
                      backgroundColor: '#161b22',
                      padding: '16px',
                      border: '1px solid #30363d',
                      marginBottom: '16px',
                    }}
                  >
                    <div style={{ color: '#c9d1d9', fontSize: '13px', marginBottom: '12px' }}>
                      Initiate a siege to contest this territory. You have 30 minutes to breach the defenses.
                    </div>
                    <ul style={{ margin: 0, padding: '0 0 0 16px', color: '#8b949e', fontSize: '11px' }}>
                      <li style={{ marginBottom: '4px' }}>Fire cannon volleys to damage fortifications</li>
                      <li style={{ marginBottom: '4px' }}>Defenders can repair walls and fortify positions</li>
                      <li style={{ marginBottom: '4px' }}>Rally your crew for damage bonuses</li>
                      <li>Capture requires 10,000 total damage</li>
                    </ul>
                  </div>
                  <button
                    onClick={handleStartSiege}
                    style={{
                      width: '100%',
                      padding: '14px',
                      backgroundColor: '#f85149',
                      color: '#ffffff',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '13px',
                      fontWeight: 'bold',
                      fontFamily: '"Sometype Mono", monospace',
                    }}
                  >
                    BEGIN SIEGE
                  </button>
                </div>
              )}
            </div>
          ) : (
            // Active siege
            <div>
              {/* Siege Status Bar */}
              <div
                style={{
                  backgroundColor: '#161b22',
                  padding: '12px',
                  border: '1px solid #30363d',
                  marginBottom: '12px',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ color: '#f85149', fontSize: '12px', fontWeight: 'bold' }}>
                    {siegeStatus.siege?.attackerName} [ATK]
                  </span>
                  <span style={{ color: getPhaseColor(siegeStatus.siege?.phase || ''), fontSize: '11px', fontWeight: 'bold' }}>
                    {getPhaseLabel(siegeStatus.siege?.phase || '')}
                  </span>
                  <span style={{ color: '#3fb950', fontSize: '12px', fontWeight: 'bold' }}>
                    [DEF] {siegeStatus.siege?.defenderName}
                  </span>
                </div>

                {/* Progress Bar */}
                <div
                  style={{
                    backgroundColor: '#21262d',
                    height: '24px',
                    position: 'relative',
                    marginBottom: '8px',
                  }}
                >
                  <div
                    style={{
                      backgroundColor: getPhaseColor(siegeStatus.siege?.phase || ''),
                      height: '100%',
                      width: `${siegeStatus.siege?.progress || 0}%`,
                      transition: 'width 0.3s ease',
                    }}
                  />
                  <div
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      color: '#ffffff',
                      fontSize: '11px',
                      fontWeight: 'bold',
                      textShadow: '0 0 4px #000',
                    }}
                  >
                    {(siegeStatus.siege?.progress || 0).toFixed(1)}% BREACHED
                  </div>
                </div>

                {/* Timer and Stats */}
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px' }}>
                  <span style={{ color: '#8b949e' }}>
                    TIME: <span style={{ color: '#ffd700' }}>{formatTime(siegeStatus.siege?.timeRemaining || 0)}</span>
                  </span>
                  <span style={{ color: '#8b949e' }}>
                    ATK: <span style={{ color: '#f85149' }}>{siegeStatus.siege?.attackerCount || 0}</span>
                    {' | '}
                    DEF: <span style={{ color: '#3fb950' }}>{siegeStatus.siege?.defenderCount || 0}</span>
                  </span>
                  <span style={{ color: '#8b949e' }}>
                    VOLLEYS: {siegeStatus.siege?.volleyCount || 0}
                  </span>
                </div>

                {/* Status Effects */}
                {(siegeStatus.siege?.isFortified || siegeStatus.siege?.isRallied) && (
                  <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                    {siegeStatus.siege?.isFortified && (
                      <span style={{ backgroundColor: '#238636', padding: '2px 8px', fontSize: '10px', color: '#fff' }}>
                        FORTIFIED
                      </span>
                    )}
                    {siegeStatus.siege?.isRallied && (
                      <span style={{ backgroundColor: '#f0883e', padding: '2px 8px', fontSize: '10px', color: '#fff' }}>
                        RALLIED
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* Action Message */}
              {actionMessage && (
                <div
                  style={{
                    backgroundColor: '#1c2128',
                    padding: '10px',
                    border: '1px solid #ffd700',
                    marginBottom: '12px',
                    textAlign: 'center',
                  }}
                >
                  <span style={{ color: '#ffd700', fontSize: '12px', fontWeight: 'bold' }}>
                    {actionMessage}
                  </span>
                </div>
              )}

              {/* Join Siege or Actions */}
              {!playerSide ? (
                <div
                  style={{
                    display: 'flex',
                    gap: '12px',
                    marginBottom: '12px',
                  }}
                >
                  <button
                    onClick={() => handleJoinSiege('attacker')}
                    style={{
                      flex: 1,
                      padding: '12px',
                      backgroundColor: '#f85149',
                      color: '#ffffff',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      fontFamily: '"Sometype Mono", monospace',
                    }}
                  >
                    JOIN ATTACKERS
                  </button>
                  <button
                    onClick={() => handleJoinSiege('defender')}
                    style={{
                      flex: 1,
                      padding: '12px',
                      backgroundColor: '#238636',
                      color: '#ffffff',
                      border: 'none',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      fontFamily: '"Sometype Mono", monospace',
                    }}
                  >
                    JOIN DEFENDERS
                  </button>
                </div>
              ) : playerSide === 'attacker' ? (
                // Attacker Actions
                <div style={{ marginBottom: '12px' }}>
                  <div style={{ color: '#8b949e', fontSize: '10px', marginBottom: '8px' }}>
                    ATTACKER ACTIONS
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={handleFireVolley}
                      disabled={cooldownRemaining > 0}
                      style={{
                        flex: 2,
                        padding: '14px',
                        backgroundColor: cooldownRemaining > 0 ? '#21262d' : '#f85149',
                        color: cooldownRemaining > 0 ? '#8b949e' : '#ffffff',
                        border: 'none',
                        cursor: cooldownRemaining > 0 ? 'not-allowed' : 'pointer',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        fontFamily: '"Sometype Mono", monospace',
                      }}
                    >
                      {cooldownRemaining > 0 
                        ? `RELOADING... ${(cooldownRemaining / 1000).toFixed(1)}s`
                        : 'FIRE CANNONS'}
                    </button>
                    <button
                      onClick={handleRallyAttackers}
                      style={{
                        flex: 1,
                        padding: '14px',
                        backgroundColor: '#f0883e',
                        color: '#ffffff',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '11px',
                        fontWeight: 'bold',
                        fontFamily: '"Sometype Mono", monospace',
                      }}
                    >
                      RALLY
                    </button>
                  </div>
                </div>
              ) : (
                // Defender Actions
                <div style={{ marginBottom: '12px' }}>
                  <div style={{ color: '#8b949e', fontSize: '10px', marginBottom: '8px' }}>
                    DEFENDER ACTIONS
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => handleDefendAction('repair')}
                      disabled={cooldownRemaining > 0}
                      style={{
                        flex: 1,
                        padding: '12px',
                        backgroundColor: cooldownRemaining > 0 ? '#21262d' : '#238636',
                        color: cooldownRemaining > 0 ? '#8b949e' : '#ffffff',
                        border: 'none',
                        cursor: cooldownRemaining > 0 ? 'not-allowed' : 'pointer',
                        fontSize: '11px',
                        fontWeight: 'bold',
                        fontFamily: '"Sometype Mono", monospace',
                      }}
                    >
                      REPAIR
                    </button>
                    <button
                      onClick={() => handleDefendAction('fortify')}
                      disabled={cooldownRemaining > 0}
                      style={{
                        flex: 1,
                        padding: '12px',
                        backgroundColor: cooldownRemaining > 0 ? '#21262d' : '#1f6feb',
                        color: cooldownRemaining > 0 ? '#8b949e' : '#ffffff',
                        border: 'none',
                        cursor: cooldownRemaining > 0 ? 'not-allowed' : 'pointer',
                        fontSize: '11px',
                        fontWeight: 'bold',
                        fontFamily: '"Sometype Mono", monospace',
                      }}
                    >
                      FORTIFY
                    </button>
                    <button
                      onClick={() => handleDefendAction('rally')}
                      disabled={cooldownRemaining > 0}
                      style={{
                        flex: 1,
                        padding: '12px',
                        backgroundColor: cooldownRemaining > 0 ? '#21262d' : '#f0883e',
                        color: cooldownRemaining > 0 ? '#8b949e' : '#ffffff',
                        border: 'none',
                        cursor: cooldownRemaining > 0 ? 'not-allowed' : 'pointer',
                        fontSize: '11px',
                        fontWeight: 'bold',
                        fontFamily: '"Sometype Mono", monospace',
                      }}
                    >
                      RALLY
                    </button>
                  </div>
                  {cooldownRemaining > 0 && (
                    <div style={{ color: '#8b949e', fontSize: '10px', marginTop: '4px', textAlign: 'center' }}>
                      Cooldown: {(cooldownRemaining / 1000).toFixed(1)}s
                    </div>
                  )}
                </div>
              )}

              {/* Combat Log */}
              <div
                style={{
                  backgroundColor: '#0d1117',
                  border: '1px solid #30363d',
                  height: '150px',
                  overflow: 'auto',
                }}
              >
                <div
                  style={{
                    backgroundColor: '#161b22',
                    padding: '6px 10px',
                    borderBottom: '1px solid #30363d',
                    position: 'sticky',
                    top: 0,
                  }}
                >
                  <span style={{ color: '#8b949e', fontSize: '10px' }}>COMBAT LOG</span>
                </div>
                <div style={{ padding: '8px' }}>
                  {combatLog.length === 0 ? (
                    <div style={{ color: '#484f58', fontSize: '11px', fontStyle: 'italic' }}>
                      Awaiting combat actions...
                    </div>
                  ) : (
                    combatLog.map((entry) => (
                      <div
                        key={entry.id}
                        style={{
                          fontSize: '11px',
                          marginBottom: '4px',
                          color: entry.type === 'system' 
                            ? '#ffd700' 
                            : entry.isCritical 
                              ? '#f85149' 
                              : entry.isMiss 
                                ? '#484f58' 
                                : entry.type === 'defense' 
                                  ? '#3fb950' 
                                  : '#c9d1d9',
                          fontWeight: entry.isCritical || entry.type === 'system' ? 'bold' : 'normal',
                        }}
                      >
                        {entry.message}
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
